
import React, { useState } from 'react';
import { loginUser, registerUser, resetUserPassword } from '../services/firebaseService';
import { Sparkles, Mail, Lock, User, Loader2, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';

type AuthView = 'LOGIN' | 'SIGNUP' | 'FORGOT_PASS';

export const AuthPage: React.FC = () => {
  const [view, setView] = useState<AuthView>('LOGIN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState(''); // Only for signup
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setLoading(true);

    try {
      if (view === 'LOGIN') {
        await loginUser(email, password, rememberMe);
      } else if (view === 'SIGNUP') {
        await registerUser(email, password, name);
      } else if (view === 'FORGOT_PASS') {
        await resetUserPassword(email);
        setSuccessMsg("Password reset email sent! Check your inbox.");
        setLoading(false);
        return; 
      }
    } catch (err: any) {
      console.error(err);
      let msg = "An unexpected error occurred.";
      if (err.code === 'auth/invalid-credential') msg = "Invalid email or password.";
      if (err.code === 'auth/email-already-in-use') msg = "Email is already registered.";
      if (err.code === 'auth/weak-password') msg = "Password should be at least 6 characters.";
      if (err.code === 'auth/invalid-email') msg = "Please enter a valid email address.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-transparent">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6 animate-bounce-slow">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg shadow-indigo-500/30">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
        </div>
        <h2 className="text-center text-4xl font-extrabold text-slate-800 tracking-tight drop-shadow-sm">
          {view === 'LOGIN' && 'Sign in to MyESALON Order Automation'}
          {view === 'SIGNUP' && 'Create your account'}
          {view === 'FORGOT_PASS' && 'Reset Password'}
        </h2>
        <p className="mt-3 text-center text-lg text-slate-600 font-light">
          {view === 'LOGIN' && 'Automate your onboarding workflow'}
          {view === 'SIGNUP' && 'Start automating your document processing today'}
          {view === 'FORGOT_PASS' && 'Enter your email to receive reset instructions'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-panel py-8 px-4 shadow-2xl sm:rounded-3xl sm:px-10 relative overflow-hidden">
          {/* Decorative background blob */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <form className="space-y-6 relative z-10" onSubmit={handleSubmit}>
            
            {/* Name Field - Signup Only */}
            {view === 'SIGNUP' && (
              <div>
                <label htmlFor="name" className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1">Full Name</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="glass-input block w-full pl-10 sm:text-sm rounded-xl py-3 outline-none transition-all text-slate-800 font-medium placeholder:text-slate-400"
                    placeholder="John Doe"
                  />
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1">Email address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glass-input block w-full pl-10 sm:text-sm rounded-xl py-3 outline-none transition-all text-slate-800 font-medium placeholder:text-slate-400"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Password Field - Hidden for Forgot Password */}
            {view !== 'FORGOT_PASS' && (
              <div>
                <label htmlFor="password" className="block text-xs font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1">Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="glass-input block w-full pl-10 sm:text-sm rounded-xl py-3 outline-none transition-all text-slate-800 font-medium placeholder:text-slate-400"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}

            {/* Remember Me & Forgot Password Link */}
            {view === 'LOGIN' && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded cursor-pointer"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600 cursor-pointer select-none">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button type="button" onClick={() => { setView('FORGOT_PASS'); setError(null); setSuccessMsg(null); }} className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors">
                    Forgot password?
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            {error && (
              <div className="rounded-xl bg-red-50/80 border border-red-100 p-4 flex items-start gap-3 backdrop-blur-sm">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div className="text-sm text-red-700 font-medium">{error}</div>
              </div>
            )}

            {successMsg && (
              <div className="rounded-xl bg-green-50/80 border border-green-100 p-4 flex items-start gap-3 backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="text-sm text-green-700 font-medium">{successMsg}</div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-indigo-500/30 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  view === 'LOGIN' ? 'Sign in' : 
                  view === 'SIGNUP' ? 'Create Account' : 'Send Reset Link'
                )}
              </button>
            </div>
          </form>

          {/* Footer Toggles */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300/50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white/50 backdrop-blur-md rounded text-slate-500">
                  {view === 'LOGIN' ? 'New here?' : 'Already have an account?'}
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              {view === 'LOGIN' ? (
                 <button onClick={() => { setView('SIGNUP'); setError(null); }} className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center justify-center gap-1 mx-auto transition-colors">
                   Create an account <ArrowRight className="w-4 h-4" />
                 </button>
              ) : (
                <button onClick={() => { setView('LOGIN'); setError(null); }} className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center justify-center gap-1 mx-auto transition-colors">
                  Sign in instead
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
